import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, FileEdit } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { createAdjustmentRequest } from "@/Api/timeEntryAdjustmentRequests";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { TimeEntryTypeLabel } from "@/types/timeEntry";

export type AdjustableTimeEntry = {
  id: number;
  uuid: string;
  time_entry_type_id: number;
  clocked_at: string;
};

const schema = z.object({
  requested_date: z.string({ message: "*requerido" }).min(1, "*requerido"),
  requested_time: z.string({ message: "*requerido" }).min(1, "*requerido"),
  justification: z
    .string({ message: "*requerido" })
    .min(10, "Mínimo de 10 caracteres"),
  evidence_photo: z.any().optional(),
});

type SchemaType = z.infer<typeof schema>;

const toDateInputValue = (iso: string): string => {
  const date = new Date(iso);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const toTimeInputValue = (iso: string): string => {
  const date = new Date(iso);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

export const RequestAdjustmentBtn = ({
  entry,
  onCreated,
}: {
  entry: AdjustableTimeEntry;
  onCreated?: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formState, setFormState] = useState<string>("default");
  const { toast } = useToastNavigate();

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      requested_date: toDateInputValue(entry.clocked_at),
      requested_time: toTimeInputValue(entry.clocked_at),
      justification: "",
      evidence_photo: null,
    },
  });

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      form.reset({
        requested_date: toDateInputValue(entry.clocked_at),
        requested_time: toTimeInputValue(entry.clocked_at),
        justification: "",
        evidence_photo: null,
      });
    }
  };

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");

    const formData = new FormData();
    formData.append("time_entry_id", String(entry.id));
    formData.append("requested_date", data.requested_date);
    formData.append("requested_time", data.requested_time);
    formData.append("justification", data.justification);
    if (data.evidence_photo?.[0]) {
      formData.append("evidence_photo", data.evidence_photo[0]);
    }

    const { data: apiResponse, ok } = await createAdjustmentRequest(formData);
    if (!ok) {
      setFormState("error");
      toast({
        description: apiResponse?.message || "Erro ao criar solicitação",
        variant: "destructive",
      });
      setTimeout(() => setFormState("default"), 2000);
      return;
    }

    setFormState("default");
    setIsOpen(false);
    form.reset();
    toast({
      description: "Solicitação de ajuste enviada!",
      action: <Check color="#1edc2b" />,
    });
    onCreated?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          title="Solicitar ajuste"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
        >
          <FileEdit className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Solicitar ajuste de ponto
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between rounded-md border p-3 bg-zinc-50 dark:bg-[#2c2c2c]">
          <div>
            <div className="text-xs text-muted-foreground">
              Batida selecionada
            </div>
            <div className="font-semibold">
              {new Date(entry.clocked_at).toLocaleString("pt-BR")}
            </div>
          </div>
          <Badge variant="outline">
            {TimeEntryTypeLabel[entry.time_entry_type_id]}
          </Badge>
        </div>

        <Form
          onSubmit={form.handleSubmit(formSubmit)}
          {...form}
          className="space-y-3"
        >
          <Form.Row>
            <Form.Field name="requested_date">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Data <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} type="date" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
            <Form.Field name="requested_time">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Horário correto <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} type="time" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <Form.Row>
            <Form.Field name="justification">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Justificativa <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Textarea
                      {...field}
                      placeholder="Explique o que aconteceu"
                    />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <Form.Row>
            <Form.Field name="evidence_photo">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>Comprovante (opcional)</Form.Label>
                  <Form.Control>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => field.onChange(e.target.files)}
                    />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <DialogFooter>
            <Button
              type="submit"
              className={cn("w-full", colors.bg_primary)}
              disabled={formState !== "default"}
            >
              {formState === "loading" && (
                <>
                  Enviando <LoaderSpin />
                </>
              )}
              {formState === "default" && "Enviar solicitação"}
              {formState === "error" && "Erro ao enviar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
